// --- START OF FILE public/js/firebase/leaderboard.js ---

/**
 * js/firebase/leaderboard.js v2.4 - Modular API (Partial) + Global Export
 * Gestisce le leaderboard globali (lettura e scrittura).
 * MODIFICATO: Iniziato refactoring per sintassi modulare Firebase v9+ (Firestore).
 *             La classe LeaderboardManager viene ora esportata esplicitamente su window.
 * MANTIENE: Logica di scrittura ottimizzata, funzioni di lettura.
 */

// Importa le funzioni necessarie dalla SDK di Firebase
import {
    collection,
    addDoc,
    query,
    where,
    orderBy,
    limit,
    getDocs,
    serverTimestamp
    // Timestamp // Non sembra usato direttamente qui, ma potrebbe servire per confrontare date
} from "firebase/firestore";

class LeaderboardManager {
    constructor() {
        if (!window.firebaseServices || !window.firebaseServices.db) {
            console.error("[LBM Constructor Error v2.4] Firebase Firestore service modulare non disponibile!");
            this.db = null;
            this.initializationError = true;
            throw new Error("Firestore service non disponibile per LeaderboardManager constructor.");
        }
        this.db = window.firebaseServices.db;
        // Le referenze alle collezioni verranno create al bisogno o come membri se preferito
        // this.scoresCollectionAllTimeRef = collection(this.db, 'leaderboard_alltime');
        // this.scoresCollectionWeeklyRef = collection(this.db, 'leaderboard_weekly');
        this.initializationError = false;

        this.authManager = null; // Sarà ottenuto da window.authManager
        this.profileManager = null; // Sarà ottenuto da window.profileManager

        this.displayLimit = 10;
        this.isLoading = false;
        this.isSaving = false;

        console.log("[LBM] Constructor (v2.4 - Modular API, Global Export).");
    }

    _getAuthManager() {
        if (!this.authManager && window.authManager) this.authManager = window.authManager;
        if (!this.authManager || typeof this.authManager.isAuthenticated !== 'function') { console.error("[LBM _getAuthManager] AuthManager non disponibile!"); return null; }
        return this.authManager;
    }

    _getProfileManager() {
        if (!this.profileManager && window.profileManager) this.profileManager = window.profileManager;
        if (!this.profileManager || typeof this.profileManager.getWeekId !== 'function') { console.error("[LBM _getProfileManager] ProfileManager non disponibile o senza getWeekId!"); return null; }
        return this.profileManager;
    }

    getWeekId() {
        const pm = this._getProfileManager();
        if (pm) { return pm.getWeekId(); }
        else { /* Fallback */ const now=new Date();const y=now.getUTCFullYear();const d=new Date(Date.UTC(y,now.getMonth(),now.getDate()));const dayN=d.getUTCDay()||7;d.setUTCDate(d.getUTCDate()+4-dayN);const yS=new Date(Date.UTC(d.getUTCFullYear(),0,1));const wN=Math.ceil((((d.getTime()-yS.getTime())/86400000)+1)/7);return `${y}-W${String(wN).padStart(2,'0')}`; }
    }

    async addScoreToGlobalLeaderboards(score) {
        const authManager = this._getAuthManager();
        if (this.initializationError || this.isSaving || !this.db || !authManager || !authManager.isAuthenticated()) {
            console.warn("[LBM addScoreToGlobal Modular] Skip: Precondizioni non soddisfatte.");
            return false;
        }
        if (typeof score !== 'number' || isNaN(score) || score <= 0) {
            console.warn(`[LBM addScoreToGlobal Modular] Skip: Punteggio non valido (${score}).`);
            return false;
        }
        const userId = authManager.getUserId();
        const displayName = authManager.getUserDisplayName();
        if (!userId || !displayName) {
            console.warn("[LBM addScoreToGlobal Modular] Skip: Dati utente mancanti.");
            return false;
        }

        this.isSaving = true;
        console.log(`[LBM addScoreToGlobal Modular] Tentativo aggiunta score ${score} per ${userId} (${displayName}).`);

        const currentWeekId = this.getWeekId();
        const timestamp = serverTimestamp(); // MODULARE

        const scoreDataAllTime = { userId, displayName, score, timestamp };
        const scoreDataWeekly = { ...scoreDataAllTime, weekId: currentWeekId };

        const weeklyCollectionRef = collection(this.db, 'leaderboard_weekly'); // MODULARE
        const allTimeCollectionRef = collection(this.db, 'leaderboard_alltime'); // MODULARE

        try {
            const writePromises = [
                addDoc(weeklyCollectionRef, scoreDataWeekly), // MODULARE
                addDoc(allTimeCollectionRef, scoreDataAllTime)  // MODULARE
            ];
            await Promise.all(writePromises);
            console.log(`[LBM addScoreToGlobal Modular] Score ${score} aggiunto con successo.`);
            return true;
        } catch (error) {
            console.error(`[LBM addScoreToGlobal Modular] Errore scrittura globale per score ${score}:`, error);
            return false;
        } finally {
            this.isSaving = false;
        }
    }

    async handlePostGameSave(finalScore, isVictory = false) {
        const authManager = this._getAuthManager();
        const profileManager = this._getProfileManager();
        if (this.initializationError || !authManager || !profileManager || !authManager.isAuthenticated()) {
            console.log("[LBM handlePostGameSave Modular] Skip cache update: Utente non loggato o manager mancanti.");
            return false;
        }
        if (typeof finalScore !== 'number' || isNaN(finalScore) || finalScore < 0) {
           console.log(`[LBM handlePostGameSave Modular] Skip cache update: Punteggio non valido (${finalScore}).`);
           return false;
        }
        console.log(`[LBM handlePostGameSave Modular - SOLO CACHE UPDATE] Score: ${finalScore}, Vittoria: ${isVictory}`);
        try {
            const profileUpdated = profileManager.updateLocalCacheStats(finalScore);
            console.log(`[LBM handlePostGameSave Modular] Cache profilo aggiornata localmente? ${profileUpdated}`);
            return profileUpdated;
        } catch (error) {
             console.error("[LBM handlePostGameSave Modular] Errore imprevisto updateLocalCacheStats:", error);
             return false;
        }
    }

    async saveScore(displayName, score, metadata = {}) {
        const authManager = this._getAuthManager();
        if (this.initializationError || !authManager || !authManager.isAuthenticated()) {
            console.warn("[LBM saveScore Modular] Skip: Utente non autenticato.");
            return false;
        }
        console.log(`[LBM saveScore Modular] Chiamata esplicita per salvare score ${score} per ${displayName}`);
        return await this.addScoreToGlobalLeaderboards(score);
    }

    async getWeeklyTopScores(limitVal = this.displayLimit) {
        if (this.isLoading) { return Promise.reject('Already loading'); }
        if (this.initializationError || !this.db) { console.error("[LBM getWTS Modular] Skip: Errore init o DB."); return []; }
        this.isLoading = true;
        const currentWeekId = this.getWeekId();
        console.log(`[LBM getWTS Modular] Fetching week: ${currentWeekId}, limit: ${limitVal}`);
        try {
            const weeklyCollectionRef = collection(this.db, 'leaderboard_weekly');
            const q = query(weeklyCollectionRef, // MODULARE
                            where('weekId', '==', currentWeekId),
                            orderBy('score', 'desc'),
                            orderBy('timestamp', 'asc'),
                            limit(limitVal));
            const snapshot = await getDocs(q); // MODULARE
            const scores = snapshot.docs.map((docSnap, index) => { // docSnap invece di doc
                const data = docSnap.data();
                let dateStr = '??/??/??';
                if (data.timestamp && typeof data.timestamp.toDate === 'function') {
                    try { dateStr = data.timestamp.toDate().toLocaleDateString('it-IT',{day:'2-digit',month:'2-digit',year:'2-digit'}); } catch(e){}
                }
                return { id: docSnap.id, name: data.displayName || '???', score: data.score || 0, date: dateStr, position: index + 1 };
            });
            console.log(`[LBM getWTS Modular] Fetched ${scores.length} scores.`);
            return scores;
        } catch (error) {
            console.error('[LBM getWTS Modular] Error:', error);
            return [];
        } finally {
            this.isLoading = false;
        }
    }

    async getAllTimeTopScores(limitVal = this.displayLimit) {
        if (this.isLoading) { return Promise.reject('Already loading'); }
        if (this.initializationError || !this.db) { console.error("[LBM getATS Modular] Skip: Errore init o DB."); return []; }
        this.isLoading = true;
        console.log(`[LBM getATS Modular] Fetching limit: ${limitVal}`);
        try {
            const allTimeCollectionRef = collection(this.db, 'leaderboard_alltime');
            const q = query(allTimeCollectionRef, // MODULARE
                            orderBy('score', 'desc'),
                            orderBy('timestamp', 'asc'),
                            limit(limitVal));
            const snapshot = await getDocs(q); // MODULARE
            const scores = snapshot.docs.map((docSnap, index) => {
                const data = docSnap.data();
                let dateStr = '??/??/??';
                if (data.timestamp && typeof data.timestamp.toDate === 'function') {
                    try { dateStr = data.timestamp.toDate().toLocaleDateString('it-IT',{day:'2-digit',month:'2-digit',year:'2-digit'}); } catch(e){}
                }
                return { id: docSnap.id, name: data.displayName || '???', score: data.score || 0, date: dateStr, position: index + 1 };
            });
            console.log(`[LBM getATS Modular] Fetched ${scores.length} scores.`);
            return scores;
        } catch (error) {
            console.error('[LBM getATS Modular] Error:', error);
            return [];
        } finally {
            this.isLoading = false;
        }
    }

    async calculatePosition(score, type = 'weekly') {
        if (this.initializationError || !this.db || typeof score !== 'number' || isNaN(score)) { return -1; }
        const limitToCheck = 100;
        let q;
        if (type === 'weekly') {
            const currentWeekId = this.getWeekId();
            const weeklyCollectionRef = collection(this.db, 'leaderboard_weekly');
            q = query(weeklyCollectionRef, where('weekId', '==', currentWeekId), orderBy('score', 'desc'), orderBy('timestamp', 'asc'), limit(limitToCheck));
        } else {
            const allTimeCollectionRef = collection(this.db, 'leaderboard_alltime');
            q = query(allTimeCollectionRef, orderBy('score', 'desc'), orderBy('timestamp', 'asc'), limit(limitToCheck));
        }
        try {
            const snapshot = await getDocs(q);
            let position = 1;
            for (const docSnap of snapshot.docs) {
                const data = docSnap.data();
                if (score > data.score) {
                    return position;
                }
                position++;
            }
            return snapshot.size < limitToCheck ? snapshot.size + 1 : -1; // Se fuori dai top 100, ma ci sono meno di 100 record, è l'ultimo + 1
        } catch (error) {
            console.error(`[LBM calculatePosition Modular ${type}] Errore:`, error);
            return -1;
        }
    }
}

// Esporta esplicitamente la classe su window
window.LeaderboardManager = LeaderboardManager;

console.log("[LBM Modular] LeaderboardManager class (v2.4 - Modular API, Global Export) defined.");

// --- END OF FILE public/js/firebase/leaderboard.js ---